import { Chart, type ChartConfiguration } from 'chart.js/auto';
import dayjs from 'dayjs';
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';

import {
    type AnalyticsIncident,
    bucketMttr,
    bucketVolume,
    computeKpis,
    type FocusField,
    formatDuration,
    type GroupedSeries,
    type Kpis,
    paretoBy,
    type SeverityBucket,
    severityBuckets,
    type TrendRange,
    focusWindows as windows,
} from 'components/incidentManagement/incidentAnalytics.helpers';
import { useGetSeverityLevelsQuery } from 'store/api/oopsApi';

// ponytail: the app is hard-pinned to theme--sds_dark (index.html), and the Strata
// theme emits no runtime CSS vars, so the dark palette is inlined here for the canvas.
// Add a theme-var bridge only if a light theme is ever enabled.
// Keyed by severity id (0=Critical … 4=Informational) — stable across label changes.
const SEVERITY_COLORS_BY_ID: Record<number, string> = {
    0: '#ed5c5c',
    1: '#f58754',
    2: '#fec34e',
    3: '#539bc9',
    4: '#4fb287',
};
const UNKNOWN_COLOR = '#606574';
const MTTR_COLOR = '#4fb287';
const CARD_BG = '#23252b';
const TICK_COLOR = '#adb2bf';
const GRID_COLOR = 'rgba(255, 255, 255, 0.08)';

const colorFor = (id: number | null): string =>
    id === null ? UNKNOWN_COLOR : (SEVERITY_COLORS_BY_ID[id] ?? UNKNOWN_COLOR);

const baseScales = (stacked: boolean, yTickSuffix = '') => ({
    x: {
        stacked,
        grid: { display: false },
        ticks: { color: TICK_COLOR, font: { size: 11 } },
    },
    y: {
        stacked,
        beginAtZero: true,
        grid: { color: GRID_COLOR },
        border: { display: false },
        ticks: {
            color: TICK_COLOR,
            font: { size: 11 },
            precision: 0,
            callback: (value: number | string) => `${value}${yTickSuffix}`,
        },
    },
});

const stackedBarConfig = (grouped: GroupedSeries, indexAxis: 'x' | 'y'): ChartConfiguration => ({
    type: 'bar',
    data: {
        labels: grouped.labels,
        datasets: grouped.series.map((s) => ({
            label: s.name,
            data: s.data,
            backgroundColor: colorFor(s.id),
            borderColor: CARD_BG,
            borderWidth: 2,
            borderRadius: 3,
            stack: 'severity',
        })),
    },
    options: {
        indexAxis,
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false }, tooltip: { mode: 'index' } },
        scales: baseScales(true),
    },
});

const mttrLineConfig = (labels: string[], hours: (number | null)[]): ChartConfiguration => ({
    type: 'line',
    data: {
        labels,
        datasets: [
            {
                label: 'MTTR (hours)',
                data: hours,
                borderColor: MTTR_COLOR,
                backgroundColor: 'rgba(79, 178, 135, 0.12)',
                borderWidth: 2,
                fill: true,
                tension: 0.3,
                spanGaps: true,
                pointRadius: 0,
                pointHoverRadius: 5,
                pointBackgroundColor: MTTR_COLOR,
            },
        ],
    },
    options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            tooltip: {
                callbacks: {
                    label: (ctx) => `${ctx.parsed.y} h`,
                },
            },
        },
        scales: baseScales(false, 'h'),
    },
});

const ChartCanvas = ({
    config,
    ariaLabel,
    height,
}: {
    config: ChartConfiguration;
    ariaLabel: string;
    height: number;
}) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        // ponytail: jsdom returns null here, so charts are simply skipped under test.
        if (!canvas?.getContext('2d')) return undefined;
        const chart = new Chart(canvas, config);
        return () => chart.destroy();
    }, [config]);

    return (
        <div
            className="incident-analytics__canvas"
            style={{ height }}
            role="img"
            aria-label={ariaLabel}
        >
            <canvas ref={canvasRef} />
        </div>
    );
};

const Toggle = <T extends string>({
    value,
    options,
    onChange,
}: {
    value: T;
    options: { value: T; label: string }[];
    onChange: (value: T) => void;
}) => (
    <div className="incident-analytics__toggle" role="group">
        {options.map((option) => (
            <button
                key={option.value}
                type="button"
                className={value === option.value ? 'is-active' : ''}
                aria-pressed={value === option.value}
                onClick={() => onChange(option.value)}
            >
                {option.label}
            </button>
        ))}
    </div>
);

const Delta = ({
    value,
    suffix,
    label,
}: {
    value: number | null;
    suffix: string;
    label: string;
}) => {
    if (value === null || value === 0) {
        return <span className="incident-analytics__delta">No change</span>;
    }
    const improving = value < 0; // fewer incidents / faster recovery is better
    return (
        <span className={`incident-analytics__delta ${improving ? 'is-good' : 'is-bad'}`}>
            <span aria-hidden="true">{improving ? '↓' : '↑'}</span> {Math.abs(value)}
            {suffix} {label}
        </span>
    );
};

const Legend = ({ buckets }: { buckets: SeverityBucket[] }) => (
    <div className="incident-analytics__legend">
        {buckets.map((bucket) => (
            <span key={bucket.name}>
                <span
                    className="incident-analytics__swatch"
                    style={{ backgroundColor: colorFor(bucket.id) }}
                />
                {bucket.name}
            </span>
        ))}
    </div>
);

const Kpi = ({ label, value, children }: { label: string; value: string; children: ReactNode }) => (
    <div className="incident-analytics__kpi">
        <div className="incident-analytics__kpi-label">{label}</div>
        <div className="incident-analytics__kpi-value">{value}</div>
        {children}
    </div>
);

export const IncidentAnalytics = ({ incidents }: { incidents: AnalyticsIncident[] }) => {
    const { data: levels = [] } = useGetSeverityLevelsQuery();

    const [trendRange, setTrendRange] = useState<TrendRange>('weekly');
    const [focusRange, setFocusRange] = useState<TrendRange>('weekly');
    const [focusField, setFocusField] = useState<FocusField>('missingQualityCheck');

    // Stable "now" for the lifetime of the mount so memoized buckets don't churn.
    const now = useMemo(() => dayjs(), []);
    const kpis: Kpis = useMemo(() => computeKpis(incidents, levels, now), [incidents, levels, now]);
    const severityList = useMemo(() => severityBuckets(levels), [levels]);

    const volumeConfig = useMemo(
        () => stackedBarConfig(bucketVolume(incidents, levels, trendRange, now), 'x'),
        [incidents, levels, trendRange, now],
    );
    const mttrConfig = useMemo(() => {
        const { labels, hours } = bucketMttr(incidents, trendRange, now);
        return mttrLineConfig(labels, hours);
    }, [incidents, trendRange, now]);
    const focusConfig = useMemo(
        () => stackedBarConfig(paretoBy(incidents, levels, focusField, now, focusRange), 'y'),
        [incidents, levels, focusField, focusRange, now],
    );
    const focusWindow = focusRange === 'monthly' ? 'last 12 months' : 'last 90 days';

    return (
        <section className="incident-analytics" aria-label="Incident analytics, last 90 days">
            <div className="incident-analytics__kpis">
                <Kpi label="Mean time to recovery" value={formatDuration(kpis.mttrMs)}>
                    <Delta value={kpis.mttrDeltaPct} suffix="%" label="vs prior 90d" />
                </Kpi>
                <Kpi
                    label={kpis.topSeverities.map((s) => s.name).join(' / ') || 'High severity'}
                    value={
                        kpis.topSeverities.length
                            ? kpis.topSeverities.map((s) => s.current).join(' / ')
                            : '—'
                    }
                >
                    <Delta
                        value={kpis.topSeverityDelta}
                        suffix=""
                        label={`${kpis.topSeverities[0]?.name ?? 'top'} vs prior 90d`}
                    />
                </Kpi>
                <Kpi label="Total incidents" value={`${kpis.total}`}>
                    <Delta value={kpis.totalDeltaPct} suffix="%" label="vs prior 90d" />
                </Kpi>
            </div>

            <div className="incident-analytics__card">
                <div className="incident-analytics__card-head">
                    <h2 className="incident-analytics__title">Are we getting better or worse?</h2>
                    <Toggle
                        value={trendRange}
                        onChange={setTrendRange}
                        options={[
                            { value: 'weekly', label: '90d · weekly' },
                            { value: 'monthly', label: '12mo · monthly' },
                        ]}
                    />
                </div>
                <div className="incident-analytics__subtitle">Incident volume by severity</div>
                <Legend buckets={severityList} />
                <ChartCanvas
                    config={volumeConfig}
                    height={210}
                    ariaLabel="Incident volume per period, stacked by severity"
                />
                <div className="incident-analytics__subtitle incident-analytics__subtitle--divided">
                    Mean time to recovery (hours)
                </div>
                <ChartCanvas
                    config={mttrConfig}
                    height={130}
                    ariaLabel="Mean time to recovery in hours per period"
                />
            </div>

            <div className="incident-analytics__card">
                <div className="incident-analytics__card-head">
                    <h2 className="incident-analytics__title">Where should we focus?</h2>
                    <div className="flex__gap--small" style={{ alignItems: 'center' }}>
                        <Toggle value={focusRange} onChange={setFocusRange} options={windows} />
                        <Toggle
                            value={focusField}
                            onChange={setFocusField}
                            options={[
                                { value: 'missingQualityCheck', label: 'By quality gap' },
                                { value: 'primaryImpactedServiceName', label: 'By service' },
                            ]}
                        />
                    </div>
                </div>
                <div className="incident-analytics__subtitle">
                    Ranked by incident count over the {focusWindow}, segmented by severity — the top
                    bars cut the most incidents.
                </div>
                <ChartCanvas
                    config={focusConfig}
                    height={300}
                    ariaLabel={`Top drivers over the ${focusWindow}, ranked by count and severity`}
                />
            </div>
        </section>
    );
};
